import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Input,
  Select,
  HStack,
  Button,
  Flex,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  SimpleGrid,
  Grid,
  GridItem,
  Icon,
  InputGroup,
  InputLeftElement,
  Badge,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Divider,
  useToast,
  Link,
  Checkbox,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';

import { 
  ChevronUpIcon, 
  ChevronDownIcon 
} from '@chakra-ui/icons';

import { 
  MdFilterList, 
  MdRefresh, 
  MdSearch, 
  MdDownload, 
  MdClear,
  MdRemoveCircle,
  MdSync,
  MdGroup,
} from 'react-icons/md';

// Import the separated components
import PrecatoriosSelecionadasModal from './PrecatoriosSelecionadasModal';
import { useHandleOpenDetails } from './useHandleOpenDetails';
import { useNavigate } from 'react-router-dom';

const API_PRSE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

const PrecatoriosSelecionadas = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // Search & filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNatureza, setFilterNatureza] = useState('');
  const [filterEnte, setFilterEnte] = useState('');
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [currentPriceRange, setCurrentPriceRange] = useState([0, 0]);
  const [maxTotalValue, setMaxTotalValue] = useState(null); // null means no limit
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [sortConfig, setSortConfig] = useState({ key: 'ordem', direction: 'asc' }); // Default sort by ordem
  
  // State for tracking selected precatorios for unselection
  const [selectedForUnselect, setSelectedForUnselect] = useState([]);
  
  // Alert dialog state for unselect confirmation
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = React.useRef();

  // Modal states for PrecatoriosSelecionadasModal details
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPrecatorio, setSelectedPrecatorio] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [jurisconsultDetails, setJurisconsultDetails] = useState(null);
  const [escavadorDetails, setEscavadorDetails] = useState(null);

  // Use the custom hook for handling opening details
  const handleOpenDetails = useHandleOpenDetails(
    API_PRSE_URL,
    setSelectedPrecatorio,
    setIsModalOpen,
    setLoadingDetails,
    setDetailsError,
    setJurisconsultDetails,
    toast,
    setEscavadorDetails
  );

  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Wait a bit before clearing data to avoid UI flicker
    setTimeout(() => {
      setSelectedPrecatorio(null);
      setJurisconsultDetails(null);
      setEscavadorDetails(null);
      setDetailsError(null);
    }, 300);
  };

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const highlightColor = useColorModeValue('brand.primary', 'brand.secondary');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    const fetchPrecatorios = async () => {
      try {
        const res = await fetch(`${API_PRSE_URL}/precatorioPR/selecionados`);
        if (!res.ok) {
          throw new Error('Falha ao buscar precatórios selecionados');
        }
        
        const data = await res.json();
        setPrecatorios(data);
        
        // Initialize price range based on data
        if (data.length > 0) {
          const values = data.map(p => p.valor_atualizado);
          const minValue = Math.max(0, Math.floor(Math.min(...values)));
          const maxValue = Math.ceil(Math.max(...values));
          setPriceRange([minValue, maxValue]);
          setCurrentPriceRange([minValue, maxValue]);
        }
      } catch (err) {
        console.error(err);
        setErro(true);
        toast({
          title: 'Erro ao carregar precatórios',
          description: err.message || 'Não foi possível carregar a lista de precatórios selecionados',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPrecatorios();
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ isActive, direction }) => {
    return direction === 'asc' ? (
      <ChevronUpIcon ml={2} opacity={isActive ? 1 : 0.3} />
    ) : (
      <ChevronDownIcon ml={2} opacity={isActive ? 1 : 0.3} />
    );
  };

  // Extract unique filters from data
  const naturezas = useMemo(() => [...new Set(precatorios.map(p => p.natureza).filter(Boolean))], [precatorios]);
  const entes = useMemo(() => [...new Set(precatorios.map(p => p.ente).filter(Boolean))], [precatorios]);

  const filteredData = useMemo(() => {
    let result = [...precatorios];
  
    // Search + Filters
    result = result.filter(p =>
      p.numero_precatorio.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (!filterNatureza || p.natureza === filterNatureza) &&
      (!filterEnte || p.ente === filterEnte) &&
      p.valor_atualizado >= currentPriceRange[0] &&
      p.valor_atualizado <= currentPriceRange[1]
    );
  
    // Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
    
        // Handle 'recebimento' as a date
        if (sortConfig.key === 'recebimento') {
          const aDate = new Date(aVal.split('/').reverse().join('-'));
          const bDate = new Date(bVal.split('/').reverse().join('-'));
    
          return sortConfig.direction === 'asc'
            ? aDate - bDate
            : bDate - aDate;
        }
    
        // Handle numeric values
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
    
        // Fallback: string comparison
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
    
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    // Apply cumulative total filter
    if (maxTotalValue !== null && maxTotalValue > 0) {      
      let cumulativeTotal = 0;
      const filteredByTotal = [];
      
      for (const precatorio of result) {
        if (cumulativeTotal + precatorio.valor_atualizado <= maxTotalValue) {
          cumulativeTotal += precatorio.valor_atualizado;
          filteredByTotal.push(precatorio);
        } else {
          // Stop adding once we would exceed the max total
          break;
        }
      }
      result = filteredByTotal;
    }
    
    return result;
  }, [precatorios, searchTerm, filterNatureza, filterEnte, currentPriceRange, sortConfig, maxTotalValue]);
  
  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  // Handlers
  const handlePageChange = (direction) => {
    setCurrentPage(prev => {
      if (direction === 'next' && prev < totalPages) return prev + 1;
      if (direction === 'prev' && prev > 1) return prev - 1;
      return prev;
    });
  };

  const handlePriceRangeChange = (values) => {
    // Ensure minimum is not negative
    const minValue = Math.max(0, values[0]);
    // Ensure maximum is not less than minimum
    const maxValue = Math.max(minValue, values[1]);
    setCurrentPriceRange([minValue, maxValue]);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterNatureza('');
    setFilterEnte('');
    setCurrentPriceRange([priceRange[0], priceRange[1]]);
    setMaxTotalValue(null);
    setCurrentPage(1);
  };

  const formatCurrency = (value) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  // Calculate total value
  const totalValue = useMemo(() => {
    return filteredData.reduce((acc, item) => acc + item.valor_atualizado, 0);
  }, [filteredData]);
  
  // Calculate selected for unselect total value
  const selectedUnselectTotalValue = useMemo(() => {
    return precatorios
      .filter(p => selectedForUnselect.includes(p._id))
      .reduce((acc, item) => acc + item.valor_atualizado, 0);
  }, [precatorios, selectedForUnselect]);
  
  // Handler for unselecting precatorios
  const handleUnselectPrecatorios = async () => {
    try {
      toast({
        title: 'Removendo seleção',
        description: 'Aguarde enquanto removemos a seleção dos precatórios...',
        status: 'loading',
        duration: 3000,
        isClosable: true,
        id: 'unselect-precatorios',
      });
      
      const response = await fetch(`${API_PRSE_URL}/precatorioPR/desselecionar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ precatorios: selectedForUnselect }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Sucesso!',
          description: `${data.count} precatórios foram desmarcados.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          id: 'unselect-precatorios',
        });
        
        // Remove unselected items from the list
        setPrecatorios(precatorios.filter(p => !selectedForUnselect.includes(p._id)));
        setSelectedForUnselect([]);
        setIsAlertOpen(false);
      } else {
        throw new Error(data.message || 'Erro ao remover seleção');
      }
    } catch (error) {
      console.error('Erro ao remover seleção:', error);
      toast({
        title: 'Erro',
        description: `Erro ao remover seleção: ${error.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        id: 'unselect-precatorios',
      });
    }
  };

  return (
    <Box p={{ base: 4, md: 8 }}>
      {/* Header Section */}
      <Flex 
        direction={{ base: 'column', md: 'row' }} 
        justify="space-between" 
        align={{ base: 'flex-start', md: 'center' }} 
        mb={8}
      >
        <Box mb={{ base: 4, md: 0 }}>
          <Heading 
            fontSize={{ base: '2xl', md: '3xl' }} 
            fontWeight="bold"
            color={headingColor}
            mb={2}
          >
            Precatórios Selecionados – TJ-PR
          </Heading>
          <Text color={textColor}>
            Lista de Precatórios Selecionados do Tribunal de Justiça do Paraná
          </Text>
        </Box>
        <HStack spacing={3}>
          <Button 
            leftIcon={<Icon as={MdSync} />}
            variant="solid"
            colorScheme="purple"
            size="sm"
            onClick={() => navigate('/admin/precatorios/ba/sincronizar')}
          >
            Buscar Dados JurisConsult
          </Button>
          <Button 
            leftIcon={<Icon as={MdRefresh} />}
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Atualizar
          </Button>
          <Button 
            leftIcon={<Icon as={MdDownload} />}
            variant="solid"
            colorScheme="blue"
            size="sm"
          >
            Exportar
          </Button>
        </HStack>
      </Flex>

      {loading ? (
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody py={12} textAlign="center">
            <VStack spacing={4}>
              <Spinner size="xl" color={highlightColor} thickness="4px" />
              <Text>Carregando dados de precatórios...</Text>
            </VStack>
          </CardBody>
        </Card>
      ) : erro ? (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          Erro ao buscar os dados de precatórios. Tente novamente mais tarde.
        </Alert>
      ) : (
        <>
          {/* Stats Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Total de Precatórios</StatLabel>
                  <StatNumber>{totalItems}</StatNumber>
                  <StatHelpText>{totalItems === 1 ? 'Precatório selecionado' : 'Precatórios selecionados'}</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                <StatLabel color={textColor}>Valor Total</StatLabel>
                <StatNumber>{formatCurrency(totalValue)}</StatNumber>
                <StatHelpText>
                    {maxTotalValue !== null
                      ? `Limitado a ${formatCurrency(maxTotalValue)}`
                      : 'Soma dos valores atualizados'
                    }
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Valor Médio</StatLabel>
                  <StatNumber>
                    {totalItems > 0 
                      ? formatCurrency(totalValue / totalItems) 
                      : 'R$ 0,00'}
                  </StatNumber>
                  <StatHelpText>Por precatório</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Filters Card */}
          <Card 
            bg={cardBg}
            borderRadius="lg"
            boxShadow="sm"
            overflow="hidden"
            border="1px"
            borderColor={borderColor}
            mb={6}
          >
            <CardBody py={4}>
              <Grid 
                templateColumns={{ base: "1fr", md: "1fr 1fr auto auto" }}
                gap={4}
                alignItems="center"
              >
                <GridItem>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input 
                      placeholder="Buscar por nº precatório" 
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </InputGroup>
                </GridItem>
                
                <GridItem>
                  <HStack>
                    <Select 
                      placeholder="Filtrar por natureza"
                      value={filterNatureza}
                      onChange={(e) => {
                        setFilterNatureza(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      {naturezas.map((natureza) => (
                        <option key={natureza} value={natureza}>
                          {natureza}
                        </option>
                      ))}
                    </Select>
                    
                    <Select 
                      placeholder="Filtrar por ente"
                      value={filterEnte}
                      onChange={(e) => {
                        setFilterEnte(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      {entes.map((ente) => (
                        <option key={ente} value={ente}>
                          {ente}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                </GridItem>
                
                <GridItem>
                  <Button 
                    leftIcon={<Icon as={MdFilterList} />}
                    onClick={onOpen}
                    size="md"
                    variant="outline"
                    w="full"
                  >
                    Filtros Avançados
                  </Button>
                </GridItem>
                
                <GridItem>
                  <HStack justifyContent="flex-end">
                    <Select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      size="md"
                    >
                      {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option} por página
                        </option>
                      ))}
                    </Select>
                    
                    {(searchTerm || filterNatureza || filterEnte || 
                      currentPriceRange[0] !== priceRange[0] || 
                      currentPriceRange[1] !== priceRange[1] ||
                      maxTotalValue !== null) && (
                      <Button 
                        leftIcon={<Icon as={MdClear} />}
                        onClick={clearFilters}
                        size="md"
                        variant="ghost"
                        colorScheme="red"
                      >
                        Limpar
                      </Button>
                    )}
                  </HStack>
                </GridItem>
              </Grid>

              {/* Price Range Drawer */}
              <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
                <DrawerOverlay />
                <DrawerContent>
                  <DrawerCloseButton />
                  <DrawerHeader borderBottomWidth="1px">
                    Filtros Avançados
                  </DrawerHeader>

                  <DrawerBody>
                    <VStack spacing={6} align="stretch" pt={4}>
                      <FormControl>
                        <FormLabel fontWeight="medium">
                          Valor Total Máximo (R$)
                        </FormLabel>
                        <InputGroup size="lg">
                          <InputLeftElement pointerEvents="none">
                            <Text color="gray.500">R$</Text>
                          </InputLeftElement>
                          <Input
                            type="text"
                            placeholder="Ex: 5.000.000.000,00"
                            value={maxTotalValue ? maxTotalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              
                              // If empty, clear the filter
                              if (inputValue === '') {
                                setMaxTotalValue(null);
                                setCurrentPage(1);
                                return;
                              }
                              
                              // Remove thousand separators (dots) and replace comma with dot
                              // This handles Brazilian format: 3.500.000,00 -> 3500000.00
                              const normalizedValue = inputValue
                                .replace(/\./g, '') // Remove thousand separators
                                .replace(',', '.'); // Replace decimal comma with dot
                              
                              // Parse the normalized value
                              const numValue = parseFloat(normalizedValue);
                              
                              // Only update if it's a valid positive number
                              if (!isNaN(numValue) && numValue > 0) {
                                setMaxTotalValue(numValue);
                                setCurrentPage(1);
                              }
                            }}
                          />
                        </InputGroup>
                        <Text fontSize="sm" color="gray.500" mt={2}>
                          Limita a soma cumulativa dos precatórios conforme a ordenação atual
                        </Text>
                      </FormControl>

                      <Divider />
                      
                      <FormControl>
                        <FormLabel fontWeight="medium">
                          Faixa de Valor Individual (R$)
                        </FormLabel>
                        
                        <Flex justifyContent="space-between" alignItems="center" mb={4}>
                          <Input 
                            type="text" 
                            value={`R$ ${currentPriceRange[0].toLocaleString('pt-BR')}`}
                            onChange={(e) => {
                              const value = Number(e.target.value.replace(/[^0-9]/g, ''));
                              if (!isNaN(value)) {
                                setCurrentPriceRange([value, currentPriceRange[1]]);
                              }
                            }}
                            bg="gray.700"
                            color="white"
                            w="140px"
                          />
                          
                          <Input 
                            type="text" 
                            value={`R$ ${currentPriceRange[1].toLocaleString('pt-BR')}`}
                            onChange={(e) => {
                              const value = Number(e.target.value.replace(/[^0-9]/g, ''));
                              if (!isNaN(value)) {
                                setCurrentPriceRange([currentPriceRange[0], value]);
                              }
                            }}
                            w="140px"
                          />
                        </Flex>
                        
                        <RangeSlider
                          min={priceRange[0]}
                          max={priceRange[1]}
                          step={(priceRange[1] - priceRange[0]) / 100}
                          value={currentPriceRange}
                          onChange={handlePriceRangeChange}
                          colorScheme="blue"
                        >
                          <RangeSliderTrack>
                            <RangeSliderFilledTrack />
                          </RangeSliderTrack>
                          <RangeSliderThumb index={0} />
                          <RangeSliderThumb index={1} />
                        </RangeSlider>
                      </FormControl>

                      <Divider />

                      <Box>
                        <Text fontWeight="medium" mb={2}>Filtros Aplicados:</Text>
                        <VStack align="start" spacing={2}>
                          {searchTerm && (
                            <Badge colorScheme="blue" px={2} py={1} borderRadius="full">
                              Busca: {searchTerm}
                            </Badge>
                          )}
                          {filterNatureza && (
                            <Badge colorScheme="green" px={2} py={1} borderRadius="full">
                              Natureza: {filterNatureza}
                            </Badge>
                          )}
                          {filterEnte && (
                            <Badge colorScheme="purple" px={2} py={1} borderRadius="full">
                              Ente: {filterEnte}
                            </Badge>
                          )}
                          {(currentPriceRange[0] !== priceRange[0] || currentPriceRange[1] !== priceRange[1]) && (
                            <Badge colorScheme="orange" px={2} py={1} borderRadius="full">
                              Valor Individual: {formatCurrency(currentPriceRange[0])} - {formatCurrency(currentPriceRange[1])}
                            </Badge>
                          )}
                          {maxTotalValue !== null && (
                            <Badge colorScheme="red" px={2} py={1} borderRadius="full">
                              Valor Total Máximo: {formatCurrency(maxTotalValue)}
                            </Badge>
                          )}
                        </VStack>
                      </Box>
                    </VStack>
                  </DrawerBody>

                  <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={clearFilters}>
                      Limpar Filtros
                    </Button>
                    <Button colorScheme="blue" onClick={onClose}>
                      Aplicar
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </CardBody>
          </Card>
          
          {/* Unselect Button */}
          {selectedForUnselect.length > 0 && (
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              mb={6}
            >
              <CardBody py={3}>
                <Flex justify="space-between" align="center">
                  <Text fontWeight="medium">
                    {selectedForUnselect.length} precatório{selectedForUnselect.length > 1 ? 's' : ''} selecionado{selectedForUnselect.length > 1 ? 's' : ''} - Total: {formatCurrency(selectedUnselectTotalValue)}
                  </Text>
                  <Button
                    leftIcon={<Icon as={MdRemoveCircle} />}
                    colorScheme="red"
                    size="sm"
                    onClick={() => setIsAlertOpen(true)}
                  >
                    Remover Seleção
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          )}

          {/* Results Card */}
          <Card 
            bg={cardBg}
            borderRadius="lg"
            boxShadow="sm"
            overflow="hidden"
            border="1px"
            borderColor={borderColor}
            mb={6}
          >
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={headingColor}>
                  Precatórios Selecionados
                </Heading>
                <Badge colorScheme="green" fontSize="sm" py="1" px="2" borderRadius="md">
                  {totalItems} {totalItems === 1 ? 'resultado' : 'resultados'}
                </Badge>
              </Flex>
            </CardHeader>
            
            <CardBody p={0}>
              <Box overflowX="auto" w="100%">
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th onClick={() => handleSort('ordem')} cursor="pointer">
                        <Flex align="center">
                          Ordem
                          <SortIcon
                            isActive={sortConfig.key === 'ordem'}
                            direction={sortConfig.key === 'ordem' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('numero_precatorio')} cursor="pointer">
                        <Flex align="center">
                          Nº Precatório
                          <SortIcon
                            isActive={sortConfig.key === 'numero_precatorio'}
                            direction={sortConfig.key === 'numero_precatorio' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('natureza')} cursor="pointer">
                        <Flex align="center">
                          Natureza
                          <SortIcon
                            isActive={sortConfig.key === 'natureza'}
                            direction={sortConfig.key === 'natureza' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('orcamento')} cursor="pointer">
                        <Flex align="center">
                          Orçamento
                          <SortIcon
                            isActive={sortConfig.key === 'orcamento'}
                            direction={sortConfig.key === 'orcamento' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('recebimento')} cursor="pointer">
                        <Flex align="center">
                          Recebimento
                          <SortIcon
                            isActive={sortConfig.key === 'recebimento'}
                            direction={sortConfig.key === 'recebimento' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('prioridade')} cursor="pointer">
                        <Flex align="center">
                          Prioridade
                          <SortIcon
                            isActive={sortConfig.key === 'prioridade'}
                            direction={sortConfig.key === 'prioridade' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('valor_atualizado')} cursor="pointer">
                        <Flex align="center">
                          Valor Atualizado
                          <SortIcon
                            isActive={sortConfig.key === 'valor_atualizado'}
                            direction={sortConfig.key === 'valor_atualizado' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('ente')} cursor="pointer">
                        <Flex align="center">
                          Ente
                          <SortIcon
                            isActive={sortConfig.key === 'ente'}
                            direction={sortConfig.key === 'ente' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>
                      <Th textAlign="center">
                        <Checkbox
                          colorScheme="red"
                          isChecked={paginatedData.length > 0 && paginatedData.every(item => selectedForUnselect.includes(item._id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Select all precatorios on current page
                              const currentPageIds = paginatedData.map(item => item._id);
                              const newSelection = [...selectedForUnselect];
                              
                              // Add only ids that aren't already selected
                              currentPageIds.forEach(id => {
                                if (!newSelection.includes(id)) {
                                  newSelection.push(id);
                                }
                              });
                              
                              setSelectedForUnselect(newSelection);
                            } else {
                              // Deselect all precatorios on current page
                              const currentPageIds = paginatedData.map(item => item._id);
                              setSelectedForUnselect(selectedForUnselect.filter(id => !currentPageIds.includes(id)));
                            }
                          }}
                          size="lg"
                        />
                      </Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((item) => (
                        <Tr 
                          key={item.numero_precatorio}
                          _hover={{ bg: hoverBg }}
                          transition="background-color 0.2s"
                        >
                          <Td>{item.ordem}</Td>
                          <Td fontWeight="medium" color={highlightColor}>
                            <Link 
                              onClick={() => handleOpenDetails(item)}
                              textDecoration="underline"
                              cursor="pointer"
                              _hover={{ color: 'blue.500' }}
                            >
                              {item.numero_precatorio}
                            </Link>
                          </Td>
                          <Td>
                            <Badge colorScheme={item.natureza === 'ALIMENTAR' ? 'green' : 'purple'} variant="subtle">
                              {item.natureza}
                            </Badge>
                          </Td>
                          <Td>{item.orcamento}</Td>
                          <Td>{item.recebimento}</Td>
                          <Td>{item.prioridade || '-'}</Td>
                          <Td fontWeight="medium">R$ {item.valor_atualizado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Td>
                          <Td>{item.ente}</Td>
                          <Td textAlign="center">
                            <Checkbox 
                              colorScheme="red"
                              isChecked={selectedForUnselect.includes(item._id)}
                              onChange={() => {
                                if (selectedForUnselect.includes(item._id)) {
                                  setSelectedForUnselect(selectedForUnselect.filter(id => id !== item._id));
                                } else {
                                  setSelectedForUnselect([...selectedForUnselect, item._id]);
                                }
                              }}
                              size="lg"
                            />
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={9} textAlign="center" py={8}>
                          <VStack spacing={3}>
                            <Text color={textColor}>Nenhum precatório encontrado com os filtros selecionados</Text>
                            <Button 
                              size="sm"
                              variant="outline"
                              leftIcon={<Icon as={MdClear} />}
                              onClick={clearFilters}
                            >
                              Limpar Filtros
                            </Button>
                          </VStack>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>

            {paginatedData.length > 0 && (
              <CardFooter borderTopWidth="1px" borderColor={borderColor} justifyContent="space-between">
                <Text fontSize="sm" color={textColor}>
                  Página {currentPage} de {totalPages} ({totalItems} registros)
                </Text>
                <HStack>
                  <Button 
                    onClick={() => handlePageChange('prev')} 
                    isDisabled={currentPage === 1}
                    size="sm"
                    variant="outline"
                  >
                    Anterior
                  </Button>
                  <Button 
                    onClick={() => handlePageChange('next')} 
                    isDisabled={currentPage === totalPages}
                    size="sm"
                    variant="solid"
                    colorScheme="blue"
                  >
                    Próxima
                  </Button>
                </HStack>
              </CardFooter>
            )}
          </Card>

          {/* Info Footer */}
          <Flex 
            mt={10} 
            justify="space-between" 
            fontSize="xs" 
            color={textColor}
            borderTop="1px"
            borderColor={borderColor}
            pt={4}
          >
            <Text>Fonte: API TJ-PR | Última atualização: Hoje, 09:45</Text>
            <Text>
              Em caso de dúvidas, entre em contato com o suporte técnico
            </Text>
          </Flex>
        </>
      )}

      {/* PrecatoriosSelecionadasModal Modal Component */}
      <PrecatoriosSelecionadasModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedPrecatorio={selectedPrecatorio}
        loadingDetails={loadingDetails}
        detailsError={detailsError}
        jurisconsultDetails={jurisconsultDetails}
        escavadorDetails={escavadorDetails}
        handleOpenDetails={handleOpenDetails}
      />
      
      {/* Alert Dialog for Unselect Confirmation */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsAlertOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar Remoção de Seleção
            </AlertDialogHeader>

            <AlertDialogBody>
              Você está prestes a remover a seleção de {selectedForUnselect.length} precatório{selectedForUnselect.length > 1 ? 's' : ''} com valor total de {formatCurrency(selectedUnselectTotalValue)}.
              <br /><br />
              Esta ação irá removê-los da lista de selecionados. Tem certeza que deseja continuar?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleUnselectPrecatorios} ml={3}>
                Remover Seleção
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default PrecatoriosSelecionadas;